/**
 * v1.4.24 底部参数面板共享设施（脚本节点面板与其他节点 EditorPanel 复用）：
 * 1) useNodeTranslate——翻译逻辑（原脚本面板 v1.4.22 接线 / v1.4.23 执行状态存活机制上移）：
 *    中英互译·自动方向（中文译英文、英文译中文、混合/其他统一译中文），译文经 onResult 回写面板草稿；
 *    走任务管线 LLM 分支（feature=script-translate），modelId 必须显式携带对话模型（路由层对空 modelId
 *    有 400 拦截且未豁免 LLM feature）；翻译执行状态存模块级 zustand store（按节点 id 键控）——面板随
 *    取消选中卸载时 useState 会复位，store 存活于卸载周期之外，重开面板自动恢复 spinner/禁用态，
 *    防重入跨卸载周期生效；页面刷新自然清零（对齐不入恢复链语义）。
 * 2) isChatModel / pickTranslateModel——对话模型过滤与三级回退（精确 id → 名称含 Flash Lite → 首个 chat），
 *    脚本面板默认选型与 EditorPanel 翻译模型共用。
 * 3) clampExpandPanelSize——全屏展开面板尺寸边界（min 520×360 / max 视口 92%，对齐脚本面板 v1.4.17）。
 */
import { useCallback } from 'react';
import { create } from 'zustand';
import { toast } from 'sonner';
import { generateApi } from '@/services/generateApi';

/** 翻译可用的对话模型最小接口（agentStore.availableModels 的结构子集；图像/视频模型不可用于翻译） */
export interface TranslateChatModel {
  id: string;
  name: string;
  supportedModes?: string[];
}

interface NodeTranslateState {
  translatingIds: Record<string, boolean>;
  setTranslating: (nodeId: string, v: boolean) => void;
}

/** 翻译执行状态（按节点 id，模块级 store）：跨面板卸载周期存活（v1.4.23 语义，自脚本面板上移） */
const useNodeTranslateStore = create<NodeTranslateState>((set) => ({
  translatingIds: {},
  setTranslating: (nodeId, v) =>
    set((s) => ({ translatingIds: { ...s.translatingIds, [nodeId]: v } })),
}));

/** chat 模型判定：未声明 supportedModes 或声明中含 chat（与脚本面板历史判定同口径） */
export const isChatModel = (m: TranslateChatModel) =>
  !m.supportedModes || m.supportedModes.length === 0 || m.supportedModes.includes('chat');

/** 对话模型三级回退（精确 id → 名称含 Flash Lite → 首个 chat）：默认选型与翻译模型共用 */
export const pickTranslateModel = (chatModels: TranslateChatModel[]): TranslateChatModel | undefined =>
  chatModels.find((m) => m.id === 'grsai-gemini-3.1-flash-lite')
  || chatModels.find((m) => /flash lite/i.test(m.name))
  || chatModels[0];

/** 翻译（中英互译·自动方向）：translating 执行中标记 + translate() 触发，两底部参数面板共用 */
export function useNodeTranslate(options: {
  nodeId: string;
  /** 翻译源文案（trim 后非空才发起） */
  text: string;
  /** 对话模型 id（翻译走 LLM 分支；空值时按钮侧禁用、函数内兜底拦截） */
  modelId: string;
  /** 译文回写（替换面板草稿/提示词） */
  onResult: (translated: string) => void;
}) {
  const { nodeId, text, modelId, onResult } = options;
  const translating = useNodeTranslateStore((s) => !!s.translatingIds[nodeId]);
  const setTranslating = useNodeTranslateStore((s) => s.setTranslating);

  const translate = useCallback(async () => {
    const source = text.trim();
    if (!source || translating || !modelId) return;
    setTranslating(nodeId, true);
    try {
      const res = await generateApi.generateAsync({
        mode: 'chat',
        feature: 'script-translate',
        prompt: `请翻译以下文案：中文译为英文，英文译为中文，混合或其他语言统一译为中文。只返回译文本身，不要任何解释或前后缀：\n\n${source}`,
        modelId,
      });
      const statusResult = await generateApi.pollTaskUntilComplete(res.taskId);
      if (statusResult.status !== 'success') {
        throw new Error(statusResult.error || '翻译任务未成功');
      }
      const translated = (statusResult.result as { data?: { text?: string } } | undefined)?.data?.text;
      if (!translated) throw new Error('未收到译文');
      onResult(translated);
    } catch (e) {
      const message = e instanceof Error ? e.message : '翻译失败';
      toast.error('翻译失败', { description: message, duration: 6000 });
    } finally {
      setTranslating(nodeId, false);
    }
  }, [text, translating, modelId, onResult, nodeId, setTranslating]);

  return { translating, translate };
}

/** 全屏展开面板尺寸边界：最小 520×360，最大不超过视口 92%（对齐脚本面板 v1.4.17 图一/图二） */
const EXPAND_MIN_W = 520;
const EXPAND_MIN_H = 360;
const EXPAND_MAX_VIEWPORT_RATIO = 0.92;
export const clampExpandPanelSize = (w: number, h: number) => ({
  w: Math.max(EXPAND_MIN_W, Math.min(w, window.innerWidth * EXPAND_MAX_VIEWPORT_RATIO)),
  h: Math.max(EXPAND_MIN_H, Math.min(h, window.innerHeight * EXPAND_MAX_VIEWPORT_RATIO)),
});
